import { CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import {
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Flex,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { Card } from "../../common/components/Card.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { getUserDisplayName } from "../../common/utils/userDisplayName";
import { assignmentScheduleActions } from "../../features/assignment-schedule/assignmentScheduleSlice";
import ConnectedAppLayout from "../AppLayout.tsx";
import { routeNames } from "../common/routes";
import type {
  ManualAssignment,
  TechnicianAssignmentSchedule,
  TechnicianAssignmentScheduleStatusEnum,
} from "../generated-axios-client";
import type { RootState } from "../store";
import {
  bookingStatusLabel,
  confirmCancelBooking,
  isBookingCancellable,
  isBookingEditable,
  shownBookingStatus,
} from "./bookingStatus";
import { EditBookingDatesModal } from "./EditBookingDatesModal";
import { formatBookingRange } from "../../common/utils/formatBookingRange";
import { ScheduleTechniciansModal } from "./ScheduleTechniciansModal";
import styles from "./TechnicianSchedulePage.module.css";

const API_DATE_FORMAT = "YYYY-MM-DD";
const MANUAL_ASSIGNMENTS_SHOWN = 3;
const BOARD_SOURCE = { board: true } as const;

type ViewMode = "week" | "month" | "months";
type MonthRange = [Dayjs, Dayjs];

const MAX_RANGE_MONTHS = 12;
const DEFAULT_RANGE_MONTHS = 3;
const MONTH_KEY_FORMAT = "YYYY-MM";
// Per browser: the board reopens on the last view and the last Months range.
const SAVED_VIEW_STORAGE_KEY = "dzp.technicianSchedule.view";

type SavedView = { viewMode: ViewMode; rangeFrom: string; rangeTo: string };

const defaultMonthRange = (): MonthRange => {
  const firstMonth = dayjs().startOf("month");
  return [firstMonth, firstMonth.add(DEFAULT_RANGE_MONTHS - 1, "month")];
};

const monthsInRange = ([first, last]: MonthRange) => last.diff(first, "month") + 1;

function readSavedView(): { viewMode: ViewMode; monthRange: MonthRange } {
  const fallback = { viewMode: "week" as ViewMode, monthRange: defaultMonthRange() };
  try {
    const saved = JSON.parse(
      localStorage.getItem(SAVED_VIEW_STORAGE_KEY) ?? "null"
    ) as Partial<SavedView> | null;
    if (!saved) return fallback;
    const viewMode = (["week", "month", "months"] as const).includes(
      saved.viewMode as ViewMode
    )
      ? (saved.viewMode as ViewMode)
      : fallback.viewMode;
    const rangeFrom = dayjs(saved.rangeFrom, MONTH_KEY_FORMAT);
    const rangeTo = dayjs(saved.rangeTo, MONTH_KEY_FORMAT);
    const rangeIsValid =
      rangeFrom.isValid() &&
      rangeTo.isValid() &&
      !rangeTo.isBefore(rangeFrom) &&
      monthsInRange([rangeFrom, rangeTo]) <= MAX_RANGE_MONTHS;
    return {
      viewMode,
      monthRange: rangeIsValid ? [rangeFrom, rangeTo] : fallback.monthRange,
    };
  } catch {
    return fallback;
  }
}

function saveView(viewMode: ViewMode, [rangeFrom, rangeTo]: MonthRange) {
  const saved: SavedView = {
    viewMode,
    rangeFrom: rangeFrom.format(MONTH_KEY_FORMAT),
    rangeTo: rangeTo.format(MONTH_KEY_FORMAT),
  };
  try {
    localStorage.setItem(SAVED_VIEW_STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // storage can be unavailable (private window); the board then opens on Week
  }
}

const BAR_CLASS_BY_STATUS: Partial<
  Record<TechnicianAssignmentScheduleStatusEnum, string>
> = {
  SCHEDULED: styles.barScheduled,
  ACTIVE: styles.barActive,
  ENDING: styles.barEnding,
  COMPLETED: styles.barCompleted,
  CANCELLED: styles.barCompleted,
};

const startOfIsoWeek = (date: Dayjs) => date.subtract((date.day() + 6) % 7, "day");

const visibleRange = (
  anchor: Dayjs,
  mode: ViewMode,
  [firstMonth, lastMonth]: MonthRange
): [Dayjs, Dayjs] => {
  if (mode === "week") {
    return [startOfIsoWeek(anchor).startOf("day"), startOfIsoWeek(anchor).add(6, "day")];
  }
  if (mode === "months") {
    return [firstMonth.startOf("month"), lastMonth.endOf("month").startOf("day")];
  }
  return [anchor.startOf("month"), anchor.endOf("month").startOf("day")];
};

const VIEW_CLASS: Record<ViewMode, string> = {
  week: styles.weekView,
  month: styles.monthView,
  months: styles.monthsView,
};

type PlacedBooking = {
  booking: TechnicianAssignmentSchedule;
  firstColumn: number;
  lastColumn: number;
  lane: number;
};

// Overlapping bookings of one technician (several work orders at once) are stacked
// in lanes: each booking takes the first lane that is free on its first day.
const placeBookings = (
  bookings: TechnicianAssignmentSchedule[],
  rangeStart: Dayjs,
  dayCount: number
): { placed: PlacedBooking[]; laneCount: number } => {
  const laneEnds: number[] = [];
  const placed = [...bookings]
    .sort((first, second) => first.start_date.localeCompare(second.start_date))
    .map((booking) => {
      const firstColumn = Math.max(0, dayjs(booking.start_date).diff(rangeStart, "day"));
      const lastColumn = Math.min(
        dayCount - 1,
        dayjs(booking.end_date).diff(rangeStart, "day")
      );
      let lane = laneEnds.findIndex((laneEnd) => laneEnd < firstColumn);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(lastColumn);
      } else {
        laneEnds[lane] = lastColumn;
      }
      return { booking, firstColumn, lastColumn, lane };
    });
  return { placed, laneCount: Math.max(1, laneEnds.length) };
};

type TechnicianSchedulePageProps = ReturnType<typeof withTechnicianSchedule> &
  DispatchProp;

export const TechnicianSchedulePage = (props: TechnicianSchedulePageProps) => {
  const {
    intl,
    dispatch,
    modalApi,
    technicians,
    bookings,
    manualAssignments,
    projects,
    locations,
  } = props;
  const [savedView] = useState(readSavedView);
  const [viewMode, setViewMode] = useState<ViewMode>(savedView.viewMode);
  const [monthRange, setMonthRange] = useState<MonthRange>(savedView.monthRange);
  const [anchor, setAnchor] = useState(() => dayjs().startOf("day"));
  const [projectId, setProjectId] = useState<string | undefined>();
  const [locationId, setLocationId] = useState<string | undefined>();
  const [technicianFilter, setTechnicianFilter] = useState<number[]>([]);
  const [booking, setBooking] = useState<{
    technicianId?: number;
    startDate?: string;
  } | null>(null);
  const [editedBooking, setEditedBooking] = useState<TechnicianAssignmentSchedule | null>(
    null
  );
  const closeBooking = useCallback(() => setBooking(null), []);
  const closeEdit = useCallback(() => setEditedBooking(null), []);

  useEffect(() => saveView(viewMode, monthRange), [viewMode, monthRange]);

  const [rangeStart, rangeEnd] = visibleRange(anchor, viewMode, monthRange);
  const rangeStartKey = rangeStart.format(API_DATE_FORMAT);
  const rangeEndKey = rangeEnd.format(API_DATE_FORMAT);
  const days = useMemo(() => {
    const start = dayjs(rangeStartKey);
    const count = dayjs(rangeEndKey).diff(start, "day") + 1;
    return Array.from({ length: count }, (_, index) => start.add(index, "day"));
  }, [rangeStartKey, rangeEndKey]);
  const today = dayjs().startOf("day");

  useEffect(() => {
    dispatch(
      assignmentScheduleActions.fetchBoard({
        startDate: rangeStartKey,
        endDate: rangeEndKey,
        project: projectId,
        location: locationId,
      })
    );
  }, [dispatch, rangeStartKey, rangeEndKey, projectId, locationId]);

  useEffect(
    () => () => {
      dispatch(assignmentScheduleActions.leaveBoard());
    },
    [dispatch]
  );

  const bookingsByTechnician = useMemo(() => {
    const grouped = new Map<number, TechnicianAssignmentSchedule[]>();
    for (const item of bookings ?? []) {
      grouped.set(item.technician, [...(grouped.get(item.technician) ?? []), item]);
    }
    return grouped;
  }, [bookings]);
  const manualByTechnician = useMemo(() => {
    const grouped = new Map<number, ManualAssignment[]>();
    for (const item of manualAssignments ?? []) {
      grouped.set(item.technician, [...(grouped.get(item.technician) ?? []), item]);
    }
    return grouped;
  }, [manualAssignments]);

  // Narrowing by project or site shows only the technicians working there; without
  // those filters every technician is listed, so free ones are visible too.
  const rows = useMemo(() => {
    const narrowedToWorkPlace = projectId !== undefined || locationId !== undefined;
    return (technicians ?? [])
      .filter(
        (technician) =>
          technicianFilter.length === 0 || technicianFilter.includes(technician.id)
      )
      .filter(
        (technician) =>
          !narrowedToWorkPlace ||
          bookingsByTechnician.has(technician.id) ||
          manualByTechnician.has(technician.id)
      )
      .map((technician) => ({ id: technician.id, name: getUserDisplayName(technician) }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [
    technicians,
    technicianFilter,
    projectId,
    locationId,
    bookingsByTechnician,
    manualByTechnician,
  ]);

  const technicianOptions = useMemo(
    () =>
      (technicians ?? [])
        .map((technician) => ({
          value: technician.id,
          label: getUserDisplayName(technician),
        }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [technicians]
  );
  const projectOptions = useMemo(
    () =>
      (projects ?? [])
        .map((project) => ({ value: project.id, label: project.name ?? "—" }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [projects]
  );
  const locationOptions = useMemo(
    () =>
      (locations ?? [])
        .map((location) => ({ value: location.id, label: location.name ?? "—" }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [locations]
  );

  const technicianName = (technicianId: number) =>
    rows.find((row) => row.id === technicianId)?.name ??
    technicianOptions.find((option) => option.value === technicianId)?.label ??
    "";

  const openWorkOrder = (ticketId: string) =>
    dispatch(routerActions.navigateTo(routeNames.TICKET_VIEW, { id: ticketId }));

  // the Months view steps by its own length, e.g. Oct–Dec moves to Jan–Mar
  const shiftRange = (direction: 1 | -1) => {
    if (viewMode === "months") {
      const step = monthsInRange(monthRange) * direction;
      setMonthRange(([first, last]) => [
        first.add(step, "month"),
        last.add(step, "month"),
      ]);
      return;
    }
    setAnchor((current) => current.add(direction, viewMode));
  };

  const showToday = () => {
    if (viewMode === "months") {
      const firstMonth = dayjs().startOf("month");
      setMonthRange([firstMonth, firstMonth.add(monthsInRange(monthRange) - 1, "month")]);
      return;
    }
    setAnchor(dayjs().startOf("day"));
  };

  const rangeLabelByView: Record<ViewMode, () => string> = {
    week: () =>
      `${intl.formatDate(rangeStart.toDate(), { day: "numeric", month: "short" })} – ${intl.formatDate(rangeEnd.toDate(), { day: "numeric", month: "short", year: "numeric" })}`,
    month: () => intl.formatDate(rangeStart.toDate(), { month: "long", year: "numeric" }),
    months: () =>
      `${intl.formatDate(rangeStart.toDate(), { month: "short", year: "numeric" })} – ${intl.formatDate(rangeEnd.toDate(), { month: "short", year: "numeric" })}`,
  };
  const rangeLabel = rangeLabelByView[viewMode]();

  // a column per month in the Months view, spanning that month's days
  const monthBands = useMemo(() => {
    if (viewMode !== "months") return [];
    const bands: { key: string; label: string; firstColumn: number; dayCount: number }[] =
      [];
    days.forEach((day, index) => {
      if (index === 0 || day.date() === 1) {
        bands.push({
          key: day.format(MONTH_KEY_FORMAT),
          label: intl.formatDate(day.toDate(), { month: "long", year: "numeric" }),
          firstColumn: index,
          dayCount: 0,
        });
      }
      bands[bands.length - 1].dayCount += 1;
    });
    return bands;
  }, [viewMode, days, intl]);

  const startsMonth = (day: Dayjs, index: number) =>
    viewMode === "months" && index > 0 && day.date() === 1;

  const gridTemplateColumns = `var(--technician-column) repeat(${days.length}, minmax(var(--day-min-width), 1fr))`;

  const renderBar = ({ booking: item, firstColumn, lastColumn, lane }: PlacedBooking) => {
    const menuItems = [
      { key: "open", label: intl.formatMessage({ id: "schedule_board_open_wo" }) },
      ...(isBookingEditable(item)
        ? [{ key: "edit", label: intl.formatMessage({ id: "schedule_edit_dates" }) }]
        : []),
      ...(isBookingCancellable(item)
        ? [
            {
              key: "cancel",
              danger: true,
              label: intl.formatMessage({ id: "schedule_board_cancel_booking" }),
            },
          ]
        : []),
    ];
    const tooltip = [
      `#${item.ticket_number ?? "—"} ${item.ticket_name ?? ""}`.trim(),
      item.project_name,
      formatBookingRange(intl, item.start_date, item.end_date),
      bookingStatusLabel(intl, item),
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <Dropdown
        key={item.id}
        trigger={["click"]}
        menu={{
          items: menuItems,
          onClick: ({ key }) => {
            if (key === "open") openWorkOrder(item.ticket);
            if (key === "edit") setEditedBooking(item);
            if (key === "cancel")
              confirmCancelBooking({
                booking: item,
                technicianName: technicianName(item.technician),
                intl,
                modalApi,
                dispatch,
                source: BOARD_SOURCE,
              });
          },
        }}
      >
        <Tooltip title={tooltip}>
          <button
            type="button"
            className={`${styles.bar} ${BAR_CLASS_BY_STATUS[shownBookingStatus(item)] ?? ""}`}
            style={{
              gridColumn: `${firstColumn + 2} / ${lastColumn + 3}`,
              gridRow: lane + 1,
            }}
          >
            <span className={styles.barLabel}>
              {viewMode === "week"
                ? `#${item.ticket_number ?? "—"}${item.ticket_name ? ` ${item.ticket_name}` : ""}`
                : (item.ticket_number ?? "—")}
            </span>
          </button>
        </Tooltip>
      </Dropdown>
    );
  };

  const renderManualAssignments = (technicianId: number) => {
    const assignments = manualByTechnician.get(technicianId) ?? [];
    if (assignments.length === 0) return null;
    const shown = assignments.slice(0, MANUAL_ASSIGNMENTS_SHOWN);
    const hidden = assignments.slice(MANUAL_ASSIGNMENTS_SHOWN);
    return (
      <Flex gap={4} wrap align="center" className={styles.manualAssignments}>
        <Typography.Text type="secondary" className={styles.manualLabel}>
          {intl.formatMessage({ id: "schedule_board_also_assigned" })}
        </Typography.Text>
        {shown.map((assignment) => (
          <Tooltip key={assignment.ticket} title={assignment.ticket_name}>
            <Tag
              className={styles.manualTag}
              onClick={() => openWorkOrder(assignment.ticket)}
            >
              #{assignment.ticket_number ?? "—"}
            </Tag>
          </Tooltip>
        ))}
        {hidden.length > 0 && (
          <Tooltip
            title={hidden.map((assignment) => `#${assignment.ticket_number}`).join(", ")}
          >
            <Tag>+{hidden.length}</Tag>
          </Tooltip>
        )}
      </Flex>
    );
  };

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "schedule_board_title" })}
        subtitle={intl.formatMessage({ id: "schedule_board_subtitle" })}
        extra={
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() => setBooking({})}
          >
            {intl.formatMessage({ id: "schedule_technicians" })}
          </Button>
        }
      >
        <Card>
          <Flex vertical gap="middle">
            <Flex justify="space-between" align="center" gap="middle" wrap>
              <Space wrap>
                <Segmented<ViewMode>
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    {
                      value: "week",
                      label: intl.formatMessage({ id: "schedule_board_week" }),
                    },
                    {
                      value: "month",
                      label: intl.formatMessage({ id: "schedule_board_month" }),
                    },
                    {
                      value: "months",
                      label: intl.formatMessage({ id: "schedule_board_months" }),
                    },
                  ]}
                />
                {viewMode === "months" && (
                  <DatePicker.RangePicker
                    picker="month"
                    format="MMM YYYY"
                    allowClear={false}
                    value={monthRange}
                    aria-label={intl.formatMessage({ id: "schedule_board_month_range" })}
                    disabledDate={(month, { from }) =>
                      from !== undefined &&
                      Math.abs(
                        month.startOf("month").diff(from.startOf("month"), "month")
                      ) >= MAX_RANGE_MONTHS
                    }
                    onChange={(range) => {
                      if (range?.[0] && range[1]) {
                        setMonthRange([
                          range[0].startOf("month"),
                          range[1].startOf("month"),
                        ]);
                      }
                    }}
                  />
                )}
                <Button icon={<LeftOutlined />} onClick={() => shiftRange(-1)} />
                <Button onClick={showToday}>
                  {intl.formatMessage({ id: "schedule_board_today" })}
                </Button>
                <Button icon={<RightOutlined />} onClick={() => shiftRange(1)} />
                <Typography.Text strong>{rangeLabel}</Typography.Text>
              </Space>
              <Space wrap>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  className={styles.filter}
                  placeholder={intl.formatMessage({ id: "material_usage_all_projects" })}
                  options={projectOptions}
                  value={projectId}
                  onChange={setProjectId}
                />
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  className={styles.filter}
                  placeholder={intl.formatMessage({ id: "material_usage_all_sites" })}
                  options={locationOptions}
                  value={locationId}
                  onChange={setLocationId}
                />
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  className={styles.technicianFilter}
                  placeholder={intl.formatMessage({
                    id: "schedule_board_all_technicians",
                  })}
                  options={technicianOptions}
                  value={technicianFilter}
                  onChange={setTechnicianFilter}
                />
              </Space>
            </Flex>

            <Spin spinning={bookings === null || technicians === null}>
              <div className={styles.scroller}>
                <div className={`${styles.board} ${VIEW_CLASS[viewMode]}`}>
                  <div className={styles.headerRow} style={{ gridTemplateColumns }}>
                    <div
                      className={styles.headerCorner}
                      style={{
                        gridRow: monthBands.length > 0 ? "1 / span 2" : undefined,
                      }}
                    >
                      {intl.formatMessage({ id: "schedule_column_technician" })}
                    </div>
                    {monthBands.map((band) => (
                      <div
                        key={band.key}
                        className={styles.monthBand}
                        style={{
                          gridColumn: `${band.firstColumn + 2} / span ${band.dayCount}`,
                          gridRow: 1,
                        }}
                      >
                        <span className={styles.monthBandLabel}>{band.label}</span>
                      </div>
                    ))}
                    {days.map((day, index) => (
                      <div
                        key={day.valueOf()}
                        className={[
                          styles.dayHeader,
                          day.day() === 0 || day.day() === 6 ? styles.weekend : "",
                          day.isSame(today, "day") ? styles.today : "",
                          startsMonth(day, index) ? styles.monthStart : "",
                        ].join(" ")}
                        style={monthBands.length > 0 ? { gridRow: 2 } : undefined}
                      >
                        <span className={styles.weekday}>
                          {intl.formatDate(day.toDate(), { weekday: "short" })}
                        </span>
                        <span>{day.date()}</span>
                      </div>
                    ))}
                  </div>

                  {rows.length === 0 && bookings !== null && (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={intl.formatMessage({ id: "schedule_board_empty" })}
                    />
                  )}

                  {rows.map((row, rowIndex) => {
                    const { placed, laneCount } = placeBookings(
                      bookingsByTechnician.get(row.id) ?? [],
                      rangeStart,
                      days.length
                    );
                    return (
                      <div
                        key={row.id}
                        className={`${styles.technicianRow} ${
                          rowIndex % 2 === 1 ? styles.technicianRowAlternate : ""
                        }`}
                        style={{
                          gridTemplateColumns,
                          gridTemplateRows: `repeat(${laneCount}, minmax(var(--lane-height), auto))`,
                        }}
                      >
                        <div
                          className={styles.nameCell}
                          style={{ gridRow: `1 / span ${laneCount}` }}
                        >
                          <Typography.Text strong ellipsis>
                            {row.name}
                          </Typography.Text>
                          {renderManualAssignments(row.id)}
                        </div>
                        {days.map((day, index) => {
                          const bookable = !day.isBefore(today, "day");
                          return (
                            <button
                              type="button"
                              key={day.valueOf()}
                              aria-label={intl.formatMessage(
                                { id: "schedule_board_book_day" },
                                {
                                  name: row.name,
                                  date: intl.formatDate(day.toDate(), {
                                    day: "numeric",
                                    month: "short",
                                  }),
                                }
                              )}
                              disabled={!bookable}
                              className={[
                                styles.dayCell,
                                day.day() === 0 || day.day() === 6 ? styles.weekend : "",
                                day.isSame(today, "day") ? styles.today : "",
                                startsMonth(day, index) ? styles.monthStart : "",
                              ].join(" ")}
                              style={{
                                gridColumn: index + 2,
                                gridRow: `1 / span ${laneCount}`,
                              }}
                              onClick={() =>
                                setBooking({
                                  technicianId: row.id,
                                  startDate: day.format(API_DATE_FORMAT),
                                })
                              }
                            />
                          );
                        })}
                        {placed.map(renderBar)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Spin>

            <Flex gap="middle" wrap align="center">
              {(["SCHEDULED", "ACTIVE", "ENDING", "COMPLETED"] as const).map((status) => (
                <Flex key={status} gap={6} align="center">
                  <span
                    className={`${styles.legendSwatch} ${BAR_CLASS_BY_STATUS[status]}`}
                  />
                  <Typography.Text type="secondary">
                    {intl.formatMessage({
                      id: `schedule_status_${status.toLowerCase()}`,
                    })}
                  </Typography.Text>
                </Flex>
              ))}
              <Typography.Text type="secondary">
                {intl.formatMessage({ id: "schedule_board_click_hint" })}
              </Typography.Text>
            </Flex>
          </Flex>
        </Card>
      </PageTitleContainer>
      <ScheduleTechniciansModal
        open={booking !== null}
        intl={intl}
        initialTechnicianIds={booking?.technicianId ? [booking.technicianId] : undefined}
        initialStartDate={booking?.startDate}
        source={BOARD_SOURCE}
        onClose={closeBooking}
      />
      <EditBookingDatesModal
        intl={intl}
        booking={editedBooking}
        source={BOARD_SOURCE}
        onClose={closeEdit}
      />
    </ConnectedAppLayout>
  );
};

const withTechnicianSchedule = (state: RootState) => ({
  intl: state.intl.datetimeIntl as IntlShape,
  modalApi: state.appFeedback.modal,
  technicians: state.assignmentSchedule.technicians,
  bookings: state.assignmentSchedule.boardBookings,
  manualAssignments: state.assignmentSchedule.manualAssignments,
  projects: state.ticketData.projects,
  locations: state.ticketData.locations,
});

export const ConnectedTechnicianSchedulePage =
  connect(withTechnicianSchedule)(TechnicianSchedulePage);
export default ConnectedTechnicianSchedulePage;
